import React from "react";
import * as Icon from "react-feather";
import { toast as alert } from "react-toastify";

import "./imported-files-modal.css";

import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import ToolsModal from "../kit/ToolsModal";
import ToolsRowMenu from "../kit/ToolsRowMenu";
import StatementWorkspaceModal from "./StatementWorkspaceModal";

const FILE_KINDS = {
	BANK_STATEMENT: "bank_statement",
	GL_REFERENCE: "gl_reference",
	CASEWARE_REFERENCE: "caseware_reference",
};

class ImportedFilesModal extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			loading: true,
			bankStatements: [],
			shortfallsByStatementId: {},
			referenceFiles: [],
			unreadStatements: [],
			filedAgainstAnotherPeriod: 0,
			statementBeingWorkedOn: null,
			fileBeingRemoved: null,
			expandedSectionKeys: UtilityService.readStoredJson({ key: this.expandedSectionsStorageKey({ clientId: props.clientId }), fallback: [] }),
		};
	}

	shortfallOf = ({ statement }) => this.state.shortfallsByStatementId[statement.id] || null;

	hasAnUnexplainedDifference = ({ statement }) => {
		const shortfall = this.shortfallOf({ statement });
		const hasOne = !!(shortfall && !shortfall.couldNotBeChecked);
		return hasOne;
	};

	describeDifference = ({ statement }) => {
		const shortfall = this.shortfallOf({ statement });
		const difference = UtilityService.formatSignedCurrency({ value: Math.abs(shortfall.shortBy) });
		return difference;
	};

	renderWarningFlag = ({ text }) => (
		<span className="if-modal__flag if-modal__flag--warn">
			<Icon.AlertTriangle size={12} /> {text}
		</span>
	);

	failedItsBalanceCheck = ({ statement }) => {
		const failed = statement.parseStatus !== "unverified" && !statement.integrityPass;
		return failed;
	};

	statementNeedsAttention = ({ statement }) => {
		const needsAttention = this.hasAnUnexplainedDifference({ statement }) || this.failedItsBalanceCheck({ statement });
		return needsAttention;
	};

	async componentDidMount() {
		await this.loadFiles();
	}

	expandedSectionsStorageKey = ({ clientId }) => `bookkeeping_shelf_expanded_${clientId}`;

	isSectionCollapsed = ({ key }) => !this.state.expandedSectionKeys.includes(key);

	onToggleSection = ({ key }) => {
		const { expandedSectionKeys } = this.state;
		const isExpanded = expandedSectionKeys.includes(key);
		const updatedKeys = isExpanded ? expandedSectionKeys.filter(storedKey => storedKey !== key) : [...expandedSectionKeys, key];

		UtilityService.writeStoredJson({ key: this.expandedSectionsStorageKey({ clientId: this.props.clientId }), value: updatedKeys });
		this.update({ expandedSectionKeys: updatedKeys });
	};

	loadFiles = async () => {
		const { clientId, wpfEngagementId } = this.props;

		await this.update({ loading: true });
		const files = await BookkeepingService.listImportedFiles({ clientId, wpfEngagementId });

		if (!files || files.error) {
			await this.update({ loading: false });
			alert.error(`The statements and files could not be read (${(files && files.error) || "no response"}).`);
			return;
		}

		await this.update({
			loading: false,
			bankStatements: files.bankStatements || [],
			referenceFiles: files.referenceFiles || [],
			unreadStatements: files.unreadStatements || [],
			filedAgainstAnotherPeriod: files.filedAgainstAnotherPeriod || 0,
		});

		await this.loadShortfalls();
	};

	loadShortfalls = async () => {
		const { wpfEngagementId } = this.props;
		if (!wpfEngagementId) return;

		const answer = await BookkeepingService.fetchStatementsThatDoNotReconcile({ wpfEngagementId });
		if (answer.error) return;

		const shortfallsByStatementId = {};
		for (const shortfall of answer.statements) shortfallsByStatementId[shortfall.statementId] = shortfall;
		await this.update({ shortfallsByStatementId });
	};

	onOpenTheStatement = ({ statement }) => this.update({ statementBeingWorkedOn: statement });

	onCloseTheStatement = () => this.update({ statementBeingWorkedOn: null });

	onReadingChanged = async () => {
		await this.loadFiles();
		if (this.props.onTransactionsChanged) await this.props.onTransactionsChanged();
	};

	onPickUpUnreadStatements = () => {
		const { onPickUpUnreadStatements } = this.props;
		const statementFiles = this.state.unreadStatements.map(statementFile => ({
			name: statementFile.fileName,
			documentId: statementFile.documentId,
			whyItStalled: statementFile.whyItStalled,
		}));
		const accountLabels = BookkeepingService.listAccountLabelsOnStatements({ statements: this.state.bankStatements });

		onPickUpUnreadStatements({ statementFiles, accountLabels });
	};

	onAskToRemove = ({ file, kind, addAnotherAfterwards = false }) =>
		this.update({ fileBeingRemoved: { ...file, kind, addAnotherAfterwards } });

	onCancelRemove = () => this.update({ fileBeingRemoved: null });

	onConfirmRemove = async () => {
		const { clientId, onFilesChanged } = this.props;
		const file = this.state.fileBeingRemoved;
		if (!file) return;

		await this.update({ fileBeingRemoved: null, loading: true });

		const result = await BookkeepingService.removeImportedFile({
			clientId,
			kind: file.kind,
			statementId: file.kind === FILE_KINDS.BANK_STATEMENT ? file.id : null,
			documentId: file.kind === FILE_KINDS.BANK_STATEMENT ? null : file.documentId,
		});

		if (!result || result.error) {
			await this.update({ loading: false });
			alert.error((result && result.error) || "That file could not be removed.");
			return;
		}

		const archivedCount = result.archivedTransactionCount || 0;
		if (archivedCount > 0) {
			alert.info(`${archivedCount} transaction${archivedCount === 1 ? "" : "s"} moved to Archived and left the trial balance.`, { autoClose: 8000 });
		} else {
			alert.success("That file was removed.");
		}

		await this.loadFiles();
		if (onFilesChanged) onFilesChanged();

		if (file.addAnotherAfterwards) this.startAddingFiles({ kind: file.kind });
	};

	startAddingFiles = ({ kind }) => {
		const { onAddFiles, onClose } = this.props;
		onClose();
		onAddFiles({ kind });
	};

	onAddStatementsClicked = () => this.startAddingFiles({ kind: FILE_KINDS.BANK_STATEMENT });

	onAddReferenceClicked = () => this.startAddingFiles({ kind: FILE_KINDS.GL_REFERENCE });

	onDownloadClicked = async ({ file }) => {
		const downloaded = await BookkeepingService.downloadImportedFile({ clientId: this.props.clientId, documentId: file.documentId, fileName: file.fileName });
		if (downloaded && downloaded.error) alert.error(`That file could not be opened (${downloaded.error}).`);
	};

	onReplaceClicked = ({ file, kind }) => this.onAskToRemove({ file, kind, addAnotherAfterwards: true });

	listStatementActions = ({ statement }) => [
		{ label: "Open beside the statement", onClick: () => this.onOpenTheStatement({ statement }) },
		{ label: "Replace with another file", onClick: () => this.onReplaceClicked({ file: statement, kind: FILE_KINDS.BANK_STATEMENT }) },
		{ label: "Remove from the books", danger: true, onClick: () => this.onAskToRemove({ file: statement, kind: FILE_KINDS.BANK_STATEMENT }) },
	];

	listReferenceActions = ({ file }) => [
		{ label: "Open the file", onClick: () => this.onDownloadClicked({ file }) },
		{ label: "Replace with another file", onClick: () => this.onReplaceClicked({ file, kind: file.category }) },
		{ label: "Remove", danger: true, onClick: () => this.onAskToRemove({ file, kind: file.category }) },
	];

	renderStatus = ({ statement }) => {
		if (this.hasAnUnexplainedDifference({ statement })) {
			return this.renderWarningFlag({ text: `${this.describeDifference({ statement })} unexplained` });
		}

		if (this.failedItsBalanceCheck({ statement })) {
			const delta = statement.integrityDelta;
			const amountOff = delta != null ? UtilityService.formatSignedCurrency({ value: Math.abs(delta) }) : "an unknown amount";
			return this.renderWarningFlag({ text: `Off by ${amountOff}` });
		}

		const nothingBookedFromIt = (statement.bookedTransactionCount || 0) === 0;
		if (nothingBookedFromIt) {
			return <span className="if-modal__flag if-modal__flag--neutral" title="Filed on the record, but nothing from it is in the books yet">Not in the books</span>;
		}

		if (statement.parseStatus === "unverified") {
			return <span className="if-modal__flag if-modal__flag--neutral">Not balance-checked</span>;
		}

		if (statement.parseStatus === "corrected") {
			return <span className="if-modal__flag if-modal__flag--neutral" title="Balances, on a reading a person corrected">Corrected</span>;
		}

		return null;
	};

	renderRowMenu = ({ items }) => {
		if (this.props.isSignedOff) {
			return <span className="if-modal__locked" title="This period has been signed off, so its source files cannot be changed">Signed off</span>;
		}
		return <ToolsRowMenu items={items} />;
	};

	renderShortfall = ({ statement }) => {
		const shortfall = this.shortfallOf({ statement });
		if (!shortfall) return null;

		if (shortfall.couldNotBeChecked) {
			return (
				<div className="if-modal__note if-modal__note--muted">
					<Icon.HelpCircle size={13} className="if-modal__note-icon" />
					<p className="if-modal__note-text">{shortfall.couldNotBeChecked}</p>
				</div>
			);
		}

		const difference = this.describeDifference({ statement });
		const statementMovedMore = shortfall.shortBy > 0;

		return (
			<div className="if-modal__note">
				<div className="if-modal__note-line">
					<Icon.AlertTriangle size={13} className="if-modal__note-icon" />
					<p className="if-modal__note-text">
						{statementMovedMore ? (
							<>
								This statement moves <strong>{difference}</strong> more than the transactions booked from it, so something printed on it was never read in.
							</>
						) : (
							<>
								The transactions booked from this statement move <strong>{difference}</strong> more than the statement does, so something is in the books it does not show.
							</>
						)}
					</p>
					<button type="button" className="tk-text-action if-modal__note-action" onClick={() => this.onOpenTheStatement({ statement })}>
						Open beside the statement
					</button>
				</div>
			</div>
		);
	};

	renderUnreadStatements = () => {
		const { unreadStatements } = this.state;
		const { isSignedOff, onPickUpUnreadStatements } = this.props;

		if (unreadStatements.length === 0 || !onPickUpUnreadStatements) return null;

		return (
			<div className="tk-notice tk-notice--warning if-modal__unread">
				<div className="tk-notice__head">
					<span className="tk-notice__title">
						<Icon.AlertTriangle size={14} />
						{unreadStatements.length} statement{unreadStatements.length === 1 ? "" : "s"} never made it into the books
					</span>
					{!isSignedOff && (
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={this.onPickUpUnreadStatements}>
							Pick {unreadStatements.length === 1 ? "it" : "them"} back up
						</button>
					)}
				</div>
				<p className="tk-notice__detail">
					These files were uploaded and kept, but nothing on the record points at them. Picking them back up reads them again, and anything
					still unreadable can be entered by hand.
				</p>
				<div className="if-modal__unread-list">
					{unreadStatements.map(statementFile => (
						<div key={statementFile.documentId} className="if-modal__unread-row" title={statementFile.whyItStalled}>
							<span className="if-modal__unread-name">{statementFile.fileName}</span>
							<span className="if-modal__unread-reason">{statementFile.whyItStalled}</span>
						</div>
					))}
				</div>
			</div>
		);
	};

	renderStatementsThisPeriodCannotSee = () => {
		const { filedAgainstAnotherPeriod, bankStatements } = this.state;
		if (filedAgainstAnotherPeriod === 0) return null;

		const onTheShelf = filedAgainstAnotherPeriod + bankStatements.length;
		const other = filedAgainstAnotherPeriod === 1 ? "statement sits" : "statements sit";

		return (
			<p className="if-modal__elsewhere" title={`This client has ${onTheShelf} statements on its record. ${bankStatements.length} fall in this fiscal period.`}>
				{filedAgainstAnotherPeriod} more {other} outside this fiscal period.
			</p>
		);
	};

	renderStatementRow = statement => {
		const span = UtilityService.describeDateSpan({ start: statement.periodStart, end: statement.periodEnd, fallback: "Period not read" });
		const fileName = statement.fileName || "Untitled statement";

		return (
			<div key={statement.id} className="if-modal__row" title={fileName}>
				<div className="if-modal__row-grid">
					<span className="if-modal__row-period">
						<span className="if-modal__row-heading">{span.heading}</span>
						{span.detail && <span className="if-modal__row-days">{span.detail}</span>}
					</span>
					<span className="if-modal__row-status">{this.renderStatus({ statement })}</span>
					{this.renderRowMenu({ items: this.listStatementActions({ statement }) })}
				</div>
				{this.renderShortfall({ statement })}
			</div>
		);
	};

	renderSectionSummary = ({ section }) => {
		const coverage = BookkeepingService.describeStatementCoverage({ count: section.count, periodStart: section.periodStart, periodEnd: section.periodEnd });

		return (
			<span className="tk-coverage">
				<Icon.CheckCircle size={12} />
				<span>{coverage}</span>
			</span>
		);
	};

	renderSectionAttention = ({ section }) => {
		const needingAttention = section.statements.filter(statement => this.statementNeedsAttention({ statement })).length;
		if (needingAttention === 0) return null;

		return this.renderWarningFlag({ text: `${needingAttention} to look at` });
	};

	renderAccountSection = section => {
		const isCollapsed = this.isSectionCollapsed({ key: section.key });
		const Chevron = isCollapsed ? Icon.ChevronRight : Icon.ChevronDown;

		return (
			<section key={section.key} className="if-modal__section">
				<div className="if-modal__section-head">
					<button
						type="button"
						className="if-modal__section-toggle"
						aria-expanded={!isCollapsed}
						title={isCollapsed ? "Show this account's statements" : "Hide this account's statements"}
						onClick={() => this.onToggleSection({ key: section.key })}
					>
						<Chevron size={16} className="if-modal__section-chevron" />
						<span className={"if-modal__section-name" + (section.isAccountKnown ? "" : " if-modal__section-name--unknown")}>{section.name}</span>
						{section.isAccountKnown && this.renderSectionSummary({ section })}
						{this.renderSectionAttention({ section })}
					</button>
				</div>
				{!isCollapsed && <div className="if-modal__rows">{section.statements.map(this.renderStatementRow)}</div>}
			</section>
		);
	};

	renderBankStatements = () => {
		const { bankStatements, unreadStatements } = this.state;

		if (bankStatements.length === 0 && unreadStatements.length === 0) {
			return <div className="if-modal__empty">No bank statements are on this client's record yet.</div>;
		}

		const sections = BookkeepingService.listStatementSectionsByAccount({ statements: bankStatements });
		return (
			<div className="if-modal__list">
				{this.renderUnreadStatements()}
				{this.renderStatementsThisPeriodCannotSee()}
				{sections.map(this.renderAccountSection)}
			</div>
		);
	};

	renderReferenceRow = file => (
		<div key={file.documentId} className="if-modal__row if-modal__row--reference">
			<div className="if-modal__row-main">
				<div className="if-modal__row-line">
					<button type="button" className="if-modal__row-heading if-modal__row-heading--openable" title="Open the file" onClick={() => this.onDownloadClicked({ file })}>
						{file.fileName}
					</button>
					<span className="if-modal__row-detail">
						<span>{file.sourceLabel}</span>
						<span>Added {UtilityService.formatDate({ value: file.uploadedAt, format: "MMM D, YYYY" })}</span>
					</span>
				</div>
			</div>
			{this.renderRowMenu({ items: this.listReferenceActions({ file }) })}
		</div>
	);

	renderReferenceFiles = () => {
		const { referenceFiles } = this.state;
		const { isSignedOff } = this.props;

		return (
			<section className="if-modal__section if-modal__section--reference">
				<div className="if-modal__section-head">
					<div className="if-modal__section-title">
						<span className="tk-section-label">Reference</span>
					</div>
					{!isSignedOff && referenceFiles.length === 0 && (
						<button type="button" className="tk-text-action" onClick={this.onAddReferenceClicked}>
							<Icon.Plus size={14} />
							Add a general ledger or CaseWare export
						</button>
					)}
				</div>
				{referenceFiles.length === 0 ? (
					<p className="if-modal__reference-empty">Nothing on file for this period. Without one, every transaction is classified by the AI instead.</p>
				) : (
					<div className="if-modal__rows">{referenceFiles.map(this.renderReferenceRow)}</div>
				)}
			</section>
		);
	};

	renderBody = () => {
		if (this.state.loading) return <div className="if-modal__empty">Reading this client's files…</div>;

		return (
			<>
				{this.renderBankStatements()}
				{this.renderReferenceFiles()}
			</>
		);
	};

	renderRemoveConfirmation = () => {
		const file = this.state.fileBeingRemoved;
		if (!file) return null;

		const isStatement = file.kind === FILE_KINDS.BANK_STATEMENT;
		const bookedCount = file.bookedTransactionCount || 0;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelRemove}>Cancel</button>
					<button type="button" className="tk-button tk-button--danger" onClick={this.onConfirmRemove}>Remove</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={this.onCancelRemove} title={isStatement ? "Remove this statement?" : "Remove this file?"} width={480} footer={footer}>
				<div className="if-modal__confirm">
					<p className="if-modal__confirm-file">{file.fileName || "Untitled file"}</p>
					{isStatement && bookedCount > 0 && (
						<p className="if-modal__confirm-line">
							<strong>{bookedCount} transaction{bookedCount === 1 ? "" : "s"}</strong> from this statement are in the books.
							They will move to Archived and leave the trial balance and the financial statements. Importing this
							statement again puts them straight back.
						</p>
					)}
					{isStatement && file.affectedPeriodCount > 1 && (
						<p className="if-modal__confirm-line if-modal__confirm-line--warn">
							This statement straddles a year-end: those transactions sit in <strong>{file.affectedPeriodCount} fiscal periods</strong>,
							and removing it changes the trial balance of every one of them — including periods you are not currently looking at.
						</p>
					)}
					{isStatement && bookedCount === 0 && (
						<p className="if-modal__confirm-line">Nothing from this statement is currently in the books, so only the statement itself is removed.</p>
					)}
					{!isStatement && (
						<p className="if-modal__confirm-line">
							This file was only used to classify transactions. Removing it leaves the books exactly as they are —
							the classifications it produced stay, and their reasons still say where they came from.
						</p>
					)}
				</div>
			</ToolsModal>
		);
	};

	renderStatementWorkspace = () => {
		const { statementBeingWorkedOn, shortfallsByStatementId } = this.state;
		if (!statementBeingWorkedOn) return null;

		return (
			<StatementWorkspaceModal
				statement={statementBeingWorkedOn}
				shortfall={shortfallsByStatementId[statementBeingWorkedOn.id] || null}
				wpfEngagementId={this.props.wpfEngagementId}
				isSignedOff={this.props.isSignedOff}
				onReadingChanged={this.onReadingChanged}
				onClose={this.onCloseTheStatement}
			/>
		);
	};

	render() {
		const { open, onClose, isSignedOff } = this.props;
		if (!open) return null;

		const footer = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Done</button>
					{!isSignedOff && (
						<button type="button" className="tk-button tk-button--primary" onClick={this.onAddStatementsClicked}>
							<Icon.Plus size={15} /> Add statements
						</button>
					)}
				</span>
			</>
		);

		return (
			<>
				<ToolsModal open={true} onClose={onClose} title="Statements & files" width={720} footer={footer}>
					{this.renderBody()}
				</ToolsModal>
				{this.renderRemoveConfirmation()}
				{this.renderStatementWorkspace()}
			</>
		);
	}
}

export default ImportedFilesModal;
